import { Color, MathUtils } from 'three';
import { calculateVoxelBounds, OnlyBot, OnlyBotLayer, OnlyBotMaterial, Point3 } from '@animavirtuality/onlybots-core';
import type { VoxelBotMaterial } from '@/lib/types';
import { usePreset } from '@/lib/presets';
import { processAnchor } from '@/lib/anchor';
import { negative } from '@/lib/numbers';

export const formatVoxelBotMaterials = (
    center: Point3,
    materials: OnlyBotMaterial[],
    layers: OnlyBotLayer[]
): VoxelBotMaterial[] =>
    materials.map((material, i) => {
        return {
            voxels: layers
                .filter(({ material }) => material === i)
                .flatMap(({ voxels }) =>
                    voxels.map((voxel) => ({
                        position: [voxel.x - center.x, voxel.y - center.y, negative(voxel.z - center.z)],
                        particle: {
                            factor: MathUtils.randInt(20, 100),
                            speed: MathUtils.randFloat(0.01, 1),
                            xFactor: MathUtils.randFloatSpread(80),
                            yFactor: MathUtils.randFloatSpread(40),
                            zFactor: MathUtils.randFloatSpread(40),
                            radius: 0.1,
                        },
                    }))
                ),
            color: new Color(
                material.color[0] / 255,
                material.color[1] / 255,
                material.color[2] / 255
            ).convertSRGBToLinear(),
            preset: usePreset(material.preset),
        };
    });

export const formatVoxelMaterialsFromBot = (bot: OnlyBot): VoxelBotMaterial[] => {
    const { min, max } = calculateVoxelBounds(bot.voxels());
    const anchor = processAnchor(bot.anchor);
    return formatVoxelBotMaterials(
        new Point3((max.x + min.x + anchor.x) / 2, (max.y + min.y) / 2 + anchor.y, (max.z + min.z - anchor.z) / 2),
        bot.materials,
        bot.layers
    );
};
